import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Share2, User, Heart, Zap, Briefcase, BookOpen, Building2, FileText, Award,
  Rocket, TrendingUp, Info, Loader2,
} from "lucide-react";

const COLS = [
  { type: "interest", label: "Interests", icon: Heart, color: "rose" },
  { type: "skill", label: "Aptitude & Skills", icon: Zap, color: "amber" },
  { type: "career", label: "Careers", icon: Briefcase, color: "cyan" },
  { type: "course", label: "Courses", icon: BookOpen, color: "indigo" },
  { type: "college", label: "Colleges", icon: Building2, color: "emerald" },
  { type: "exam", label: "Entrance Exams", icon: FileText, color: "amber" },
  { type: "internship", label: "Internships", icon: Rocket, color: "indigo" },
  { type: "job", label: "Career Outcomes", icon: TrendingUp, color: "emerald" },
];
const C = {
  rose: "border-rose-400/25 bg-rose-400/5 text-rose-200",
  amber: "border-amber-400/25 bg-amber-400/5 text-amber-200",
  cyan: "border-cyan-400/30 bg-cyan-400/8 text-cyan-100",
  indigo: "border-indigo-400/25 bg-indigo-400/5 text-indigo-200",
  emerald: "border-emerald-400/25 bg-emerald-400/5 text-emerald-200",
};

export default function CareerGraphPage() {
  const [graph, setGraph] = useState(null);
  const [why, setWhy] = useState(null);
  const [loadingWhy, setLoadingWhy] = useState(false);

  useEffect(() => { api.get("/career-graph").then((r) => setGraph(r.data)).catch(() => {}); }, []);

  const openWhy = async (category, name) => {
    setLoadingWhy(true);
    setWhy({ name, loading: true });
    try {
      const { data } = await api.get("/career-graph/why", { params: { category, name } });
      setWhy(data);
    } catch { setWhy(null); }
    finally { setLoadingWhy(false); }
  };

  if (!graph) return <div className="p-8 text-slate-400">Building your Career Graph...</div>;

  const byType = (t) => graph.nodes.filter((n) => n.type === t);
  const empty = graph.nodes.length <= 1;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={Share2} eyebrow="Your Career Graph"
        title="The intelligence behind your recommendations"
        desc="A live map connecting your interests, skills, careers, courses, colleges and outcomes — grounded in your verified profile." />

      {empty ? (
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-8 text-center">
          <Share2 className="mx-auto mb-3 h-8 w-8 text-cyan-400" />
          <p className="text-white font-semibold">Your graph is almost empty</p>
          <p className="text-sm text-slate-400 mt-1">Complete the AI assessment to build your personalized Career Graph.</p>
        </div>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-cyan-400/20 bg-gradient-to-r from-[#0F2A33] to-[#131C2E] p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400/15">
              <User className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <p className="font-semibold text-white">{graph.nodes[0].label}'s Career Graph</p>
              <p className="text-xs text-slate-400 font-mono-data">{graph.stats.nodes} nodes · {graph.stats.edges} connections · grounded in verified data</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COLS.map((col) => {
              const items = byType(col.type);
              if (items.length === 0) return null;
              const Icon = col.icon;
              return (
                <div key={col.type} data-testid={`graph-col-${col.type}`} className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-300" />
                    <p className="text-sm font-semibold text-white">{col.label}</p>
                    <span className="ml-auto text-xs text-slate-500">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((n, i) => (
                      <motion.div key={n.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        data-testid="graph-node"
                        className={`rounded-xl border px-3 py-2 text-xs ${C[col.color]}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{n.label}</span>
                          {n.type === "career" && (
                            <button data-testid="graph-why-btn" onClick={() => openWhy("career", n.label)}
                              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white/20">
                              <Info className="h-3 w-3" /> Why
                            </button>
                          )}
                        </div>
                        {n.meta?.match != null && <p className="mt-0.5 font-mono-data text-[10px] opacity-70">{n.meta.match}% match</p>}
                        {n.meta?.score != null && <p className="mt-0.5 font-mono-data text-[10px] opacity-70">{n.meta.score}% aptitude</p>}
                        {n.meta?.probability != null && <p className="mt-0.5 font-mono-data text-[10px] opacity-70">{n.meta.probability}% chance · {n.meta.bucket}</p>}
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!why} onOpenChange={(o) => { if (!o) setWhy(null); }}>
        <DialogContent className="bg-[#131C2E] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Why we recommend: {why?.name}</DialogTitle></DialogHeader>
          {loadingWhy ? (
            <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></div>
          ) : why && why.reasons ? (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300">{why.confidence}% confidence</span>
                <span className="text-xs text-slate-500">Sources: {why.sources.join(", ")}</span>
              </div>
              <ul className="space-y-2">
                {why.reasons.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-300">
                    <Award className="h-4 w-4 shrink-0 text-cyan-400 mt-0.5" /> {r}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-500 italic">Grounded in your verified profile data — not a guarantee of outcomes.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
